import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Nav } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import * as FaIcons from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useConductoresSearch } from '../../hooks/useConductoresSearch';
import { useVehiculosSearch } from '../../hooks/useVehiculosSearch';
import { 
  getClientes, 
  getTMS, 
  getRegiones, 
  getComunas 
} from '@shared/helpers/api/mantenedores/generales';
import { getElementos } from '@shared/helpers/api/mantenedores/generalesCrud';
import 'react-datepicker/dist/react-datepicker.css';

// Registrar locale español
registerLocale('es', es);

const CreateRutaModal = ({ 
  show, 
  onHide, 
  onCreateRuta, 
  user,
  loading,
  permisos
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    ruta: '',
    usuario_movil_id: '',
    tms_id: '',
    fecha: new Date(),
    ppu_vehiculo: '',
    tipo_precio: 'variable',
    precio_fijo: ''
  });

  // Estados del formulario de guía
  const [guiaData, setGuiaData] = useState({
    guia: '',
    cliente_id: '',
    centro_despacho_id: '',
    servicio_spread_id: '',
    tamano_spread_id: '',
    tamano_cliente_id: '',
    local_cliente_id: '',
    destinatario: {
      nombre: '',
      direccion: '',
      comuna_id: ''
    }
  });

  // Estados de control de fases
  const [currentPhase, setCurrentPhase] = useState(1); // 1: Información básica, 2: Información de guía
  const [phase1Completed, setPhase1Completed] = useState(false);

  // Estados de validación
  const [errors, setErrors] = useState({});
  const [guiaErrors, setGuiaErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Estado para mantener la opción seleccionada de vehículo
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  
  // Estado para mantener la opción seleccionada de conductor
  const [selectedConductor, setSelectedConductor] = useState(null);

  // Estados para clientes y filtros
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedComuna, setSelectedComuna] = useState(null);

  // Hooks para datos básicos (solo se usan en fase 1)
  const conductoresSearch = useConductoresSearch(user);
  const vehiculosSearch = useVehiculosSearch(user);
  
  // Estados para datos de catálogos (se cargan solo cuando se abre el modal)
  const [tmsOptions, setTmsOptions] = useState([]);
  const [serviciosSpread, setServiciosSpread] = useState([]);
  const [tamanosSpread, setTamanosSpread] = useState([]);
  const [localesCliente, setLocalesCliente] = useState([]);
  const [tamanosCliente, setTamanosCliente] = useState([]);
  const [centrosDespacho, setCentrosDespacho] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [comunas, setComunas] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);

  // Opciones para tipo de precio
  const tiposPrecio = [
    { value: 'variable', label: 'Variable' },
    { value: 'fijo', label: 'Fijo' }
  ];

  // Limpiar formulario cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setFormData({
        ruta: '',
        usuario_movil_id: '',
        tms_id: '',
        fecha: new Date(),
        ppu_vehiculo: '',
        tipo_precio: 'variable',
        precio_fijo: ''
      });
      setGuiaData({
        guia: '',
        cliente_id: '',
        centro_despacho_id: '',
        servicio_spread_id: '',
        tamano_spread_id: '',
        tamano_cliente_id: '',
        local_cliente_id: '',
        destinatario: {
          nombre: '',
          direccion: '',
          comuna_id: ''
        }
      });
      setErrors({});
      setGuiaErrors({});
      setCurrentPhase(1);
      setPhase1Completed(false);
      setSelectedVehiculo(null);
      setSelectedConductor(null);
      setSelectedRegion(null);
      setSelectedComuna(null);
      conductoresSearch.clearSearch();
      vehiculosSearch.clearSearch();
    }
  }, [show]);

  // Cargar todos los catálogos necesarios solo cuando se abre el modal
  useEffect(() => {
    const loadCatalogos = async () => {
      if (!user || !show) return;
      
      setLoadingCatalogos(true);
      setLoadingClientes(true);
      
      try {
        // Cargar todos los catálogos en paralelo
        const [
          clientesRes,
          tmsRes,
          serviciosRes,
          tamanosSpreadRes,
          localesRes,
          tamanosClienteRes,
          centrosRes,
          regionesRes,
          comunasRes
        ] = await Promise.all([
          getClientes(user),
          getTMS(user),
          getElementos(user, 'servicio-spread', { pageSize: 1000 }),
          getElementos(user, 'tamano-spread', { pageSize: 1000 }),
          getElementos(user, 'local-cliente', { pageSize: 1000 }),
          getElementos(user, 'tamano-cliente', { pageSize: 1000 }),
          getElementos(user, 'centro-despachos', { pageSize: 1000 }),
          getRegiones(user),
          getComunas(user)
        ]);

        // Establecer datos
        setClientes(clientesRes.success ? clientesRes.data || [] : []);
        setTmsOptions(tmsRes.success ? tmsRes.data || [] : []);
        setServiciosSpread(serviciosRes.success ? serviciosRes.data || [] : []);
        setTamanosSpread(tamanosSpreadRes.success ? tamanosSpreadRes.data || [] : []);
        setLocalesCliente(localesRes.success ? localesRes.data || [] : []);
        setTamanosCliente(tamanosClienteRes.success ? tamanosClienteRes.data || [] : []);
        setCentrosDespacho(centrosRes.success ? centrosRes.data || [] : []);
        setRegiones(regionesRes.success ? regionesRes.data || [] : []);
        setComunas(comunasRes.success ? comunasRes.data || [] : []);
        
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
      } finally {
        setLoadingCatalogos(false);
        setLoadingClientes(false);
      }
    };

    if (show) {
      loadCatalogos();
    }
  }, [show, user]);

  // Sincronizar estados de selección con los datos cargados
  useEffect(() => {
    // Sincronizar conductor seleccionado
    if (formData.usuario_movil_id && !selectedConductor && conductoresSearch.conductores.length > 0) {
      const foundConductor = conductoresSearch.conductores.find(c => c.value === formData.usuario_movil_id);
      if (foundConductor) {
        setSelectedConductor(foundConductor);
      }
    }

    // Sincronizar vehículo seleccionado
    if (formData.ppu_vehiculo && !selectedVehiculo && vehiculosSearch.vehiculos.length > 0) {
      const foundVehiculo = vehiculosSearch.vehiculos.find(v => v.value === formData.ppu_vehiculo);
      if (foundVehiculo) {
        setSelectedVehiculo(foundVehiculo);
      }
    }
  }, [
    formData.usuario_movil_id, 
    formData.ppu_vehiculo, 
    conductoresSearch.conductores, 
    vehiculosSearch.vehiculos,
    selectedConductor,
    selectedVehiculo
  ]);

  // Sincronizar región y comuna seleccionadas con los valores del formulario
  useEffect(() => {
    // Sincronizar comuna seleccionada
    if (guiaData.destinatario.comuna_id && !selectedComuna && comunas.length > 0) {
      const foundComuna = comunas.find(c => c.id === parseInt(guiaData.destinatario.comuna_id));
      if (foundComuna) {
        setSelectedComuna({
          value: foundComuna.id,
          label: foundComuna.comuna
        });
        
        // También sincronizar la región si no está seleccionada
        if (foundComuna.region && !selectedRegion && regiones.length > 0) {
          const foundRegion = regiones.find(r => r.id === foundComuna.region.id);
          if (foundRegion) {
            setSelectedRegion({
              value: foundRegion.id,
              label: foundRegion.region
            });
          }
        }
      }
    }
  }, [
    guiaData.destinatario.comuna_id,
    selectedComuna,
    selectedRegion,
    comunas,
    regiones
  ]);

  // Validar formulario de la primera fase
  const validatePhase1 = () => {
    const newErrors = {};

    if (!formData.ruta || formData.ruta.trim() === '') {
      newErrors.ruta = 'La identificación de la ruta es obligatoria';
    }

    if (!formData.usuario_movil_id) {
      newErrors.usuario_movil_id = 'Debe seleccionar un conductor';
    }

    if (!formData.tms_id) {
      newErrors.tms_id = 'Debe seleccionar un TMS';
    }

    if (!formData.fecha) {
      newErrors.fecha = 'La fecha es obligatoria';
    }

    if (!formData.ppu_vehiculo || formData.ppu_vehiculo.trim() === '') {
      newErrors.ppu_vehiculo = 'Debe seleccionar una patente de vehículo';
    }

    if (formData.tipo_precio === 'fijo') {
      if (!formData.precio_fijo || formData.precio_fijo === '') {
        newErrors.precio_fijo = 'El precio fijo es obligatorio cuando el tipo es fijo';
      } else if (isNaN(formData.precio_fijo) || parseFloat(formData.precio_fijo) <= 0) {
        newErrors.precio_fijo = 'Debe ingresar un precio válido mayor a 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validar formulario de la segunda fase
  const validatePhase2 = () => {
    const newErrors = {};

    if (!guiaData.guia || guiaData.guia.trim() === '') {
      newErrors.guia = 'El identificador de guía es obligatorio';
    }

    if (!guiaData.cliente_id) {
      newErrors.cliente_id = 'El cliente es obligatorio';
    }

    if (!guiaData.centro_despacho_id) {
      newErrors.centro_despacho_id = 'El centro de despacho es obligatorio';
    }

    if (!guiaData.servicio_spread_id) {
      newErrors.servicio_spread_id = 'El servicio spread es obligatorio';
    }

    if (!guiaData.tamano_spread_id) {
      newErrors.tamano_spread_id = 'El tamaño spread es obligatorio';
    }

    if (!guiaData.tamano_cliente_id) {
      newErrors.tamano_cliente_id = 'El tamaño cliente es obligatorio';
    }

    if (!guiaData.local_cliente_id) {
      newErrors.local_cliente_id = 'El local cliente es obligatorio';
    }

    if (!guiaData.destinatario.nombre || guiaData.destinatario.nombre.trim() === '') {
      newErrors['destinatario.nombre'] = 'El nombre del destinatario es obligatorio';
    }

    if (!guiaData.destinatario.direccion || guiaData.destinatario.direccion.trim() === '') {
      newErrors['destinatario.direccion'] = 'La dirección del destinatario es obligatoria';
    }

    if (!guiaData.destinatario.comuna_id) {
      newErrors['destinatario.comuna_id'] = 'La comuna del destinatario es obligatoria';
    }

    setGuiaErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo si existe
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Manejar cambios en el formulario de guía
  const handleGuiaInputChange = (field, value) => {
    if (field.includes('.')) {
      // Para campos anidados como destinatario.nombre
      const [parent, child] = field.split('.');
      setGuiaData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setGuiaData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Limpiar error del campo si existe
    if (guiaErrors[field]) {
      setGuiaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Avanzar a la siguiente fase
  const handleNextPhase = () => {
    if (currentPhase === 1) {
      if (validatePhase1()) {
        setPhase1Completed(true);
        setCurrentPhase(2);
      }
    }
  };

  // Regresar a la fase anterior
  const handlePreviousPhase = () => {
    if (currentPhase === 2) {
      setCurrentPhase(1);
    }
  };

  // Manejar envío del formulario completo
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar ambas fases
    if (!validatePhase1() || !validatePhase2()) {
      return;
    }

    setSubmitting(true);
    
    try {
      // Preparar datos para envío
      const submitData = {
        ruta: formData.ruta.trim(),
        usuario_movil_id: parseInt(formData.usuario_movil_id),
        tms_id: parseInt(formData.tms_id),
        fecha: formData.fecha.toISOString().split('T')[0],
        ppu_vehiculo: formData.ppu_vehiculo,
        tipo_precio: formData.tipo_precio,
        guia_data: {
          guia: guiaData.guia.trim(),
          cliente_id: parseInt(guiaData.cliente_id),
          centro_despacho_id: parseInt(guiaData.centro_despacho_id),
          servicio_spread_id: parseInt(guiaData.servicio_spread_id),
          tamano_spread_id: parseInt(guiaData.tamano_spread_id),
          tamano_cliente_id: parseInt(guiaData.tamano_cliente_id),
          local_cliente_id: parseInt(guiaData.local_cliente_id),
          destinatario: {
            nombre: guiaData.destinatario.nombre.trim(),
            direccion: guiaData.destinatario.direccion.trim(),
            comuna_id: parseInt(guiaData.destinatario.comuna_id)
          }
        }
      };

      // Agregar precio fijo si aplica
      if (formData.tipo_precio === 'fijo') {
        submitData.precio_fijo = parseFloat(formData.precio_fijo);
      }

      const result = await onCreateRuta(submitData);
      return result;
    } catch (error) {
      console.error('Error al crear ruta:', error);
      return { success: false, error: 'Error inesperado al crear la ruta' };
    } finally {
      setSubmitting(false);
    }
  };

  // Preparar opciones para TMS
  const tmsOptionsFormatted = tmsOptions?.map(tms => ({
    value: tms.id,
    label: tms.tms || tms.sigla || `TMS ${tms.id}`
  })) || [];

  // Combinar opciones de búsqueda con la opción seleccionada para mantenerla visible
  const getConductoresOptions = () => {
    const searchOptions = conductoresSearch.conductores || [];
    const selectedOption = selectedConductor;
    
    if (!selectedOption) {
      return searchOptions;
    }
    
    // Si la opción seleccionada ya está en los resultados de búsqueda, usar esos resultados
    const isSelectedInSearch = searchOptions.some(option => option.value === selectedOption.value);
    if (isSelectedInSearch) {
      return searchOptions;
    }
    
    // Si la opción seleccionada no está en los resultados de búsqueda, agregarla
    return [selectedOption, ...searchOptions];
  };

  // Combinar opciones de búsqueda con la opción seleccionada para mantenerla visible
  const getVehiculosOptions = () => {
    const searchOptions = vehiculosSearch.vehiculos || [];
    const selectedOption = selectedVehiculo;
    
    if (!selectedOption) {
      return searchOptions;
    }
    
    // Si la opción seleccionada ya está en los resultados de búsqueda, usar esos resultados
    const isSelectedInSearch = searchOptions.some(option => option.value === selectedOption.value);
    if (isSelectedInSearch) {
      return searchOptions;
    }
    
    // Si la opción seleccionada no está en los resultados de búsqueda, agregarla
    return [selectedOption, ...searchOptions];
  };

  // Preparar opciones para la segunda fase - usando todos los datos disponibles
  const clientesOptions = clientes.map(cliente => ({
    value: cliente.id,
    label: cliente.cliente
  }));

  const centrosDespachoOptions = (() => {
    // Usar allData para obtener todos los elementos sin paginación
    const sourceData = centrosDespacho || [];
    if (!sourceData.length) return [];
    
    // Filtrar duplicados por el campo centroDespacho y mantener solo el primero
    const seen = new Set();
    const uniqueCentros = sourceData.filter(centro => {
      const key = centro.centroDespacho || centro.centro_despacho;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    
    return uniqueCentros.map(centro => ({
      value: centro.id,
      label: centro.centroDespacho || centro.centro_despacho
    }));
  })();

  const serviciosSpreadOptions = (() => {
    const sourceData = serviciosSpread || [];
    return sourceData.map(servicio => ({
      value: servicio.id,
      label: servicio.servicio
    }));
  })();

  const tamanosSpreadOptions = (() => {
    const sourceData = tamanosSpread || [];
    return sourceData.map(tamano => ({
      value: tamano.id,
      label: tamano.tamano
    }));
  })();

  // Filtrar tamaños cliente por cliente seleccionado
  const getTamanosClienteOptions = () => {
    if (!guiaData.cliente_id) return [];
    const sourceData = tamanosCliente || [];
    
    return sourceData
      .filter(item => item.cliente?.id === parseInt(guiaData.cliente_id))
      .map(item => ({
        value: item.id,
        label: item.tamano
      }));
  };

  // Filtrar locales cliente por cliente seleccionado
  const getLocalesClienteOptions = () => {
    if (!guiaData.cliente_id) return [];
    const sourceData = localesCliente || [];
    
    // Filtrar por cliente seleccionado
    const filteredByClient = sourceData.filter(item => item.cliente?.id === parseInt(guiaData.cliente_id));
    
    // Eliminar duplicados por el campo 'local' y mantener solo el primero
    const seen = new Set();
    const uniqueLocales = filteredByClient.filter(item => {
      const key = item.local;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    
    return uniqueLocales.map(item => ({
      value: item.id,
      label: item.local
    }));
  };

  const regionesOptions = (() => {
    const sourceData = regiones || [];
    return sourceData.map(region => ({
      value: region.id,
      label: region.region
    }));
  })();

  // Obtener todas las opciones de comunas disponibles (filtradas por región si hay una seleccionada)
  const getComunasOptions = () => {
    const sourceData = comunas || [];
    
    // Si hay una región seleccionada, filtrar por esa región
    if (selectedRegion) {
      return sourceData
        .filter(item => item.region?.id === selectedRegion.value)
        .map(item => ({
          value: item.id,
          label: item.comuna
        }));
    }
    
    // Si no hay región seleccionada, devolver todas las comunas
    return sourceData.map(item => ({
      value: item.id,
      label: item.comuna
    }));
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>
          <FaIcons.FaPlus className="me-2" />
          Crear Nueva Ruta {currentPhase === 2 && '- Nueva Guía'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Indicador de fase */}
        <div className="mb-3">
          <Nav variant="pills" className="justify-content-center">
            <Nav.Item>
              <Nav.Link 
                active={currentPhase === 1}
                className={phase1Completed ? 'bg-success text-white' : ''}
              >
                <FaIcons.FaRoute className="me-1" />
                1. Información Básica
                {phase1Completed && <FaIcons.FaCheck className="ms-1" />}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                active={currentPhase === 2}
                disabled={!phase1Completed}
              >
                <FaIcons.FaFileInvoice className="me-1" />
                2. Información de Guía
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* FASE 1: Información Básica de Ruta */}
          {currentPhase === 1 && (
            <>
              <Row>
                {/* Identificación de Ruta */}
                <Col md={6} className="mb-3">
                  <Form.Label>Ruta *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.ruta}
                    onChange={(e) => handleInputChange('ruta', e.target.value)}
                    placeholder="Identificación de la ruta"
                    isInvalid={!!errors.ruta}
                    disabled={submitting}
                  />
                  {errors.ruta && (
                    <Form.Control.Feedback type="invalid">
                      {errors.ruta}
                    </Form.Control.Feedback>
                  )}
                </Col>

                {/* Fecha */}
                <Col md={6} className="mb-3">
                  <Form.Label>Fecha *</Form.Label>
                  <DatePicker
                    selected={formData.fecha}
                    onChange={(date) => handleInputChange('fecha', date)}
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    placeholderText="Seleccionar fecha"
                    className={`form-control ${errors.fecha ? 'is-invalid' : ''}`}
                    disabled={submitting}
                  />
                  {errors.fecha && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.fecha}
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Usuario Móvil/Conductor */}
                <Col md={6} className="mb-3">
                  <Form.Label>Usuario Móvil/Conductor *</Form.Label>
                  <Select
                    value={selectedConductor}
                    onChange={(selectedOption) => {
                      setSelectedConductor(selectedOption);
                      handleInputChange('usuario_movil_id', selectedOption ? selectedOption.value : '');
                      // Si se limpia la selección, también limpiar la búsqueda
                      if (!selectedOption) {
                        conductoresSearch.clearSearch();
                      }
                    }}
                    options={getConductoresOptions()}
                    onInputChange={conductoresSearch.handleSearchChange}
                    isLoading={conductoresSearch.loadingConductores}
                    placeholder="Buscar conductor..."
                    noOptionsMessage={() => 
                      conductoresSearch.searchValue.length < 2 
                        ? "Escriba al menos 2 caracteres para buscar" 
                        : "No se encontraron conductores"
                    }
                    isClearable
                    isDisabled={submitting}
                    className={errors.usuario_movil_id ? 'is-invalid' : ''}
                  />
                  {errors.usuario_movil_id && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.usuario_movil_id}
                    </div>
                  )}
                </Col>

                {/* TMS */}
                <Col md={6} className="mb-3">
                  <Form.Label>TMS *</Form.Label>
                  <Form.Select
                    value={formData.tms_id}
                    onChange={(e) => handleInputChange('tms_id', e.target.value)}
                    isInvalid={!!errors.tms_id}
                    disabled={submitting || loadingCatalogos}
                  >
                    <option value="">Seleccionar TMS</option>
                    {tmsOptionsFormatted.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.tms_id && (
                    <Form.Control.Feedback type="invalid">
                      {errors.tms_id}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Patente Vehículo */}
                <Col md={6} className="mb-3">
                  <Form.Label>Patente Vehículo *</Form.Label>
                  <Select
                    value={selectedVehiculo}
                    onChange={(selectedOption) => {
                      setSelectedVehiculo(selectedOption);
                      handleInputChange('ppu_vehiculo', selectedOption ? selectedOption.value : '');
                      // Si se limpia la selección, también limpiar la búsqueda
                      if (!selectedOption) {
                        vehiculosSearch.clearSearch();
                      }
                    }}
                    options={getVehiculosOptions()}
                    onInputChange={vehiculosSearch.handleSearchChange}
                    isLoading={vehiculosSearch.loadingVehiculos}
                    placeholder="Buscar patente..."
                    noOptionsMessage={() => 
                      vehiculosSearch.searchValue.length < 2 
                        ? "Escriba al menos 2 caracteres para buscar" 
                        : "No se encontraron vehículos"
                    }
                    isClearable
                    isDisabled={submitting}
                    className={errors.ppu_vehiculo ? 'is-invalid' : ''}
                  />
                  {errors.ppu_vehiculo && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.ppu_vehiculo}
                    </div>
                  )}
                </Col>

                {/* Tipo de Precio */}
                <Col md={6} className="mb-3">
                  <Form.Label>Tipo de Precio *</Form.Label>
                  <Form.Select
                    value={formData.tipo_precio}
                    onChange={(e) => handleInputChange('tipo_precio', e.target.value)}
                    disabled={submitting}
                  >
                    {tiposPrecio.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* Precio Fijo (condicional) */}
              {formData.tipo_precio === 'fijo' && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Precio Fijo *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.precio_fijo}
                      onChange={(e) => handleInputChange('precio_fijo', e.target.value)}
                      placeholder="Ingrese el precio fijo"
                      isInvalid={!!errors.precio_fijo}
                      disabled={submitting}
                    />
                    {errors.precio_fijo && (
                      <Form.Control.Feedback type="invalid">
                        {errors.precio_fijo}
                      </Form.Control.Feedback>
                    )}
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* FASE 2: Información de Guía */}
          {currentPhase === 2 && (
            <>
              <Row>
                {/* Identificador de Guía */}
                <Col md={6} className="mb-3">
                  <Form.Label>Identificador de Guía *</Form.Label>
                  <Form.Control
                    type="text"
                    value={guiaData.guia}
                    onChange={(e) => handleGuiaInputChange('guia', e.target.value)}
                    placeholder="Identificador de la guía"
                    isInvalid={!!guiaErrors.guia}
                    disabled={submitting}
                  />
                  {guiaErrors.guia && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.guia}
                    </Form.Control.Feedback>
                  )}
                </Col>

                {/* Cliente */}
                <Col md={6} className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={guiaData.cliente_id}
                    onChange={(e) => {
                      handleGuiaInputChange('cliente_id', e.target.value);
                      // Limpiar selecciones dependientes
                      if (guiaData.tamano_cliente_id) handleGuiaInputChange('tamano_cliente_id', '');
                      if (guiaData.local_cliente_id) handleGuiaInputChange('local_cliente_id', '');
                    }}
                    isInvalid={!!guiaErrors.cliente_id}
                    disabled={submitting || loadingClientes}
                  >
                    <option value="">Seleccionar Cliente</option>
                    {clientesOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.cliente_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.cliente_id}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Centro de Despacho */}
                <Col md={6} className="mb-3">
                  <Form.Label>Centro de Despacho *</Form.Label>
                  <Form.Select
                    value={guiaData.centro_despacho_id}
                    onChange={(e) => handleGuiaInputChange('centro_despacho_id', e.target.value)}
                    isInvalid={!!guiaErrors.centro_despacho_id}
                    disabled={submitting || loadingCatalogos}
                  >
                    <option value="">Seleccionar Centro de Despacho</option>
                    {centrosDespachoOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.centro_despacho_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.centro_despacho_id}
                    </Form.Control.Feedback>
                  )}
                </Col>

                {/* Servicio Spread */}
                <Col md={6} className="mb-3">
                  <Form.Label>Servicio Spread *</Form.Label>
                  <Form.Select
                    value={guiaData.servicio_spread_id}
                    onChange={(e) => handleGuiaInputChange('servicio_spread_id', e.target.value)}
                    isInvalid={!!guiaErrors.servicio_spread_id}
                    disabled={submitting || loadingCatalogos}
                  >
                    <option value="">Seleccionar Servicio Spread</option>
                    {serviciosSpreadOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.servicio_spread_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.servicio_spread_id}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Tamaño Spread */}
                <Col md={6} className="mb-3">
                  <Form.Label>Tamaño Spread *</Form.Label>
                  <Form.Select
                    value={guiaData.tamano_spread_id}
                    onChange={(e) => handleGuiaInputChange('tamano_spread_id', e.target.value)}
                    isInvalid={!!guiaErrors.tamano_spread_id}
                    disabled={submitting || loadingCatalogos}
                  >
                    <option value="">Seleccionar Tamaño Spread</option>
                    {tamanosSpreadOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.tamano_spread_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.tamano_spread_id}
                    </Form.Control.Feedback>
                  )}
                </Col>

                {/* Tamaño Cliente */}
                <Col md={6} className="mb-3">
                  <Form.Label>Tamaño Cliente *</Form.Label>
                  <Form.Select
                    value={guiaData.tamano_cliente_id}
                    onChange={(e) => handleGuiaInputChange('tamano_cliente_id', e.target.value)}
                    isInvalid={!!guiaErrors.tamano_cliente_id}
                    disabled={submitting || loadingCatalogos || !guiaData.cliente_id}
                  >
                    <option value="">Seleccionar Tamaño Cliente</option>
                    {getTamanosClienteOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.tamano_cliente_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.tamano_cliente_id}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Local Cliente */}
                <Col md={12} className="mb-3">
                  <Form.Label>Local Cliente *</Form.Label>
                  <Form.Select
                    value={guiaData.local_cliente_id}
                    onChange={(e) => handleGuiaInputChange('local_cliente_id', e.target.value)}
                    isInvalid={!!guiaErrors.local_cliente_id}
                    disabled={submitting || loadingCatalogos || !guiaData.cliente_id}
                  >
                    <option value="">Seleccionar Local Cliente</option>
                    {getLocalesClienteOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {guiaErrors.local_cliente_id && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors.local_cliente_id}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              {/* Información del Destinatario */}
              <h6 className="mb-3 text-muted">
                <FaIcons.FaMapMarkerAlt className="me-2" />
                Información del Destinatario
              </h6>

              <Row>
                {/* Nombre Destinatario */}
                <Col md={6} className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    value={guiaData.destinatario.nombre}
                    onChange={(e) => handleGuiaInputChange('destinatario.nombre', e.target.value)}
                    placeholder="Nombre del destinatario"
                    isInvalid={!!guiaErrors['destinatario.nombre']}
                    disabled={submitting}
                  />
                  {guiaErrors['destinatario.nombre'] && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors['destinatario.nombre']}
                    </Form.Control.Feedback>
                  )}
                </Col>

                {/* Dirección Destinatario */}
                <Col md={6} className="mb-3">
                  <Form.Label>Dirección *</Form.Label>
                  <Form.Control
                    type="text"
                    value={guiaData.destinatario.direccion}
                    onChange={(e) => handleGuiaInputChange('destinatario.direccion', e.target.value)}
                    placeholder="Dirección del destinatario"
                    isInvalid={!!guiaErrors['destinatario.direccion']}
                    disabled={submitting}
                  />
                  {guiaErrors['destinatario.direccion'] && (
                    <Form.Control.Feedback type="invalid">
                      {guiaErrors['destinatario.direccion']}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <Row>
                {/* Región */}
                <Col md={6} className="mb-3">
                  <Form.Label>Región *</Form.Label>
                  <Select
                    value={selectedRegion}
                    onChange={(selectedOption) => {
                      setSelectedRegion(selectedOption);
                      // Limpiar comuna seleccionada cuando cambia la región
                      setSelectedComuna(null);
                      handleGuiaInputChange('destinatario.comuna_id', '');
                    }}
                    options={regionesOptions}
                    placeholder="Seleccionar región..."
                    isClearable
                    isDisabled={submitting || loadingCatalogos}
                  />
                </Col>

                {/* Comuna */}
                <Col md={6} className="mb-3">
                  <Form.Label>Comuna *</Form.Label>
                  <Select
                    value={selectedComuna}
                    onChange={(selectedOption) => {
                      setSelectedComuna(selectedOption);
                      handleGuiaInputChange('destinatario.comuna_id', selectedOption ? selectedOption.value : '');
                    }}
                    options={getComunasOptions()}
                    placeholder="Seleccionar comuna..."
                    isClearable
                    isSearchable
                    isDisabled={submitting || loadingCatalogos}
                    className={guiaErrors['destinatario.comuna_id'] ? 'is-invalid' : ''}
                    noOptionsMessage={() => 
                      selectedRegion 
                        ? "No se encontraron comunas para la región seleccionada" 
                        : "Seleccione una región primero"
                    }
                  />
                  {guiaErrors['destinatario.comuna_id'] && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {guiaErrors['destinatario.comuna_id']}
                    </div>
                  )}
                </Col>
              </Row>
            </>
          )}

          {/* Loading indicator */}
          {(loadingCatalogos || vehiculosSearch.loadingVehiculos || loadingClientes || 
            loadingCatalogos) && (
            <Alert variant="info" className="small">
              <FaIcons.FaSpinner className="fa-spin me-1" />
              Cargando datos de formulario...
            </Alert>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {currentPhase === 2 && (
          <Button 
            variant="outline-secondary" 
            onClick={handlePreviousPhase}
            disabled={submitting}
          >
            <FaIcons.FaArrowLeft className="me-2" />
            Atrás
          </Button>
        )}
        
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={submitting}
        >
          Cancelar
        </Button>

        {currentPhase === 1 ? (
          <Button 
            variant="primary"
            onClick={handleNextPhase}
            disabled={submitting}
          >
            Siguiente
            <FaIcons.FaArrowRight className="ms-2" />
          </Button>
        ) : (
          <Button 
            variant="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Creando...
              </>
            ) : (
              <>
                <FaIcons.FaPlus className="me-2" />
                Crear Ruta con Guía
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

CreateRutaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onCreateRuta: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  permisos: PropTypes.object
};

CreateRutaModal.defaultProps = {
  loading: false,
  permisos: {}
};

export default CreateRutaModal;
